// Shared survey form: renders the question list and gates a submit button until
// every visible required question is answered. Extracted from SurveyRenderer so
// both the standalone `survey` step and the inline inter-trial survey (inside a
// chat block) render questions and collect answers the same way.
//
// The form owns answer state and the submit gate; the CALLER decides what
// happens on submit (advance the step, or log inline and resume the chat) and
// supplies the surrounding container (full dialog vs. overlay card).
//
// Parity with the desktop survey_dialog.py (the canonical source of truth):
//   - PAGES: page_break items split the flat list into pages (split_survey_pages),
//     with Back / Next + a progress indicator; Submit only on the last page.
//   - NESTED FOLLOW-UPS: a question's `nested` follow-ups render inline, hidden
//     until this question's answer satisfies the child `condition` (condition_met).
//     Hidden branches never gate submit and record as "None" (str(None)).
//   - VALIDATION + DISPLAY: validation_allowed/pattern, min_length/max_words,
//     min/max_selections; VAS show_value_bubble/show_endpoints; likert
//     show_point_numbers/reverse_scale.
//
// Question types (parity with survey_dialog.py):
//   heading          - display-only section label (not answered)
//   page_break       - pseudo-item that splits pages (never a response row)
//   percentage_scale - 0..100 slider, untouched = unanswered; response = value
//   likert           - radio points 1..N with optional anchors; response = N
//   multiple_choice  - single-select radios; response = option text
//   checkbox         - multi-select; response = list of checked options
//   input            - free text with optional validation; response = text

import {
  Fragment,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import type { SurveyCondition, SurveyQuestion, ValidationAllowed } from "../../api/types";
import type { SmartCheckResult } from "../../session/sessionDriver";
import { ProvenanceRecorder, provenanceNoticeShown } from "./provenance";

export type Answer = number | string | string[] | undefined;

// The question types that own an input widget (and therefore a response row).
// heading / page_break render but never gate and never produce a response.
const INPUT_TYPES = new Set([
  "percentage_scale",
  "likert",
  "multiple_choice",
  "checkbox",
  "input",
  "smart_question",
]);

export function isAnswered(q: SurveyQuestion, a: Answer): boolean {
  switch (q.type) {
    case "percentage_scale":
      return typeof a === "number";
    case "likert":
    case "multiple_choice":
      return typeof a === "string" && a.length > 0;
    case "checkbox":
      return Array.isArray(a) && a.length > 0;
    case "input":
    case "smart_question":
      return typeof a === "string" && a.trim().length > 0;
    default:
      return true; // heading / page_break / unknown: not a gate
  }
}

// ---------------------------------------------------------------------------
// Pages + flatten (mirror split_survey_pages / flatten_questions)
// ---------------------------------------------------------------------------

// Split a flat question list into pages on `page_break` pseudo-items. Empty
// pages (leading / trailing / doubled breaks) are dropped; a survey with no
// breaks is one page and behaves exactly as before pages existed. Always
// returns at least one page.
export function splitSurveyPages(questions: SurveyQuestion[]): SurveyQuestion[][] {
  const pages: SurveyQuestion[][] = [];
  let current: SurveyQuestion[] = [];
  for (const q of questions) {
    if (q.type === "page_break") {
      if (current.length) {
        pages.push(current);
        current = [];
      }
    } else {
      current.push(q);
    }
  }
  if (current.length) pages.push(current);
  return pages.length ? pages : [[]];
}

// Depth-first flatten: each question followed by its nested follow-ups
// (recursively). Order matches the on-screen order.
export function flattenQuestions(questions: SurveyQuestion[]): SurveyQuestion[] {
  const out: SurveyQuestion[] = [];
  for (const q of questions) {
    out.push(q);
    if (q.nested && q.nested.length) out.push(...flattenQuestions(q.nested));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Condition evaluator (a faithful port of condition_met in survey_dialog.py)
// ---------------------------------------------------------------------------

function asNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value === "string") {
    if (value.trim() === "") return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

// True when `answer` (the participant's current answer to the parent question)
// satisfies `condition`. Shared by the visibility pass so the online run and the
// desktop cannot diverge.
export function conditionMet(condition: SurveyCondition | undefined, answer: Answer): boolean {
  if (!condition) return false;
  // Legacy: a bare equality against the stored value (pre-operator surveys).
  if (condition.op === undefined) {
    if (condition.equals !== undefined) return answer === condition.equals;
    return false;
  }
  const op = condition.op;
  const value = condition.value;

  if (op === "any" || op === "all") {
    const wanted = Array.isArray(value) ? value : [];
    // A checkbox answer is a list of picks; a single-select (MC) answer is one
    // value - treat it as a one-item set so the same code covers both.
    const chosen = Array.isArray(answer) ? answer : answer == null ? [] : [answer];
    const chosenSet = new Set<unknown>(chosen);
    if (wanted.length === 0) return false;
    if (op === "any") return wanted.some((v) => chosenSet.has(v));
    return wanted.every((v) => chosenSet.has(v));
  }

  // Numeric comparisons (VAS % and likert point).
  const a = asNumber(answer);
  const b = asNumber(value);
  if (a === null || b === null) return false;
  switch (op) {
    case "eq":
      return a === b;
    case "lte":
      return a <= b;
    case "gte":
      return a >= b;
    case "gt":
      return a > b;
    case "lt":
      return a < b;
    default:
      return false;
  }
}

// Compute which questions are currently shown. Top-level questions are always
// visible; a follow-up is visible only when its parent is itself visible AND the
// parent's current answer satisfies the follow-up's condition. Evaluated
// top-down so a parent's visibility is settled before its children (mirrors
// _refresh_nested).
export function computeVisible(
  questions: SurveyQuestion[],
  answers: Record<string, Answer>,
): Record<string, boolean> {
  const visible: Record<string, boolean> = {};
  const rules: Array<{ qid: string; parentId: string; condition: SurveyCondition | undefined }> =
    [];
  const walk = (list: SurveyQuestion[], parentId: string | null) => {
    for (const q of list) {
      if (q.type === "page_break") continue;
      if (parentId === null) visible[q.id] = true;
      else rules.push({ qid: q.id, parentId, condition: q.condition });
      if (q.nested && q.nested.length) walk(q.nested, q.id);
    }
  };
  walk(questions, null);
  for (const { qid, parentId, condition } of rules) {
    visible[qid] = (visible[parentId] ?? false) && conditionMet(condition, answers[parentId]);
  }
  return visible;
}

function isVisible(visible: Record<string, boolean>, qid: string): boolean {
  return visible[qid] ?? true;
}

// ---------------------------------------------------------------------------
// Validation (mirror validation_flags / validation_pattern / answer_meets_constraints)
// ---------------------------------------------------------------------------

interface Flags {
  letters: boolean;
  digits: boolean;
  specials: boolean;
}

// Legacy validation_type strings -> allowed-content flags. Old surveys stored
// only one of these four strings; new surveys ALSO store explicit
// validation_allowed flags, which support every combination.
const LEGACY_VALIDATION_FLAGS: Record<string, Flags> = {
  "Number Only": { letters: false, digits: true, specials: false },
  "Text Only": { letters: true, digits: false, specials: false },
  "Alphanumeric (No Special Characters)": { letters: true, digits: true, specials: false },
  "All Characters": { letters: true, digits: true, specials: true },
};

// The allowed-content flags for an input question. New surveys store them
// explicitly (validation_allowed); legacy surveys derive them from the
// four-value validation_type string.
export function validationFlags(q: SurveyQuestion): Flags {
  const allowed = q.validation_allowed as ValidationAllowed | undefined;
  if (allowed && typeof allowed === "object") {
    return {
      letters: allowed.letters !== false,
      digits: allowed.digits !== false,
      specials: allowed.specials !== false,
    };
  }
  const legacy = LEGACY_VALIDATION_FLAGS[q.validation_type ?? "All Characters"];
  return { ...(legacy ?? LEGACY_VALIDATION_FLAGS["All Characters"]) };
}

// Strip characters an allowed-content combination forbids, as-you-type. Mirrors
// the character classes validation_pattern builds server-side (the desktop uses
// a QRegularExpressionValidator to block the same characters).
function filterByFlags(value: string, flags: Flags): string {
  const { letters, digits, specials } = flags;
  if (letters && digits && specials) return value; // no restriction
  // "Number Only": digits, decimal point, minus (loose, like the desktop's set).
  if (digits && !letters && !specials) return value.replace(/[^0-9.-]/g, "");
  if (specials) {
    // Everything except the excluded letter / digit classes.
    let excluded = "";
    if (!letters) excluded += "a-zA-Z";
    if (!digits) excluded += "0-9";
    return excluded ? value.replace(new RegExp(`[${excluded}]`, "g"), "") : value;
  }
  // No specials: allow letters / digits, and whitespace when letters are allowed.
  let allowed = "";
  if (letters) allowed += "a-zA-Z";
  if (digits) allowed += "0-9";
  if (letters) allowed += "\\s";
  if (!allowed) return value;
  return value.replace(new RegExp(`[^${allowed}]`, "g"), "");
}

// Back-compatible: filter by one of the four legacy validation_type strings.
// Still exported/used by callers and tests that pass the legacy string.
export function filterInput(value: string, validationType?: string): string {
  const flags = LEGACY_VALIDATION_FLAGS[validationType ?? "All Characters"];
  return filterByFlags(value, flags ?? LEGACY_VALIDATION_FLAGS["All Characters"]);
}

// Advanced per-question constraints (min length, max words, min/max selections).
// True when the answer satisfies them. An empty answer passes (the Required
// check handles emptiness); constraints bite only once an answer exists.
export function answerMeetsConstraints(
  q: SurveyQuestion,
  { text, selectionCount }: { text?: string; selectionCount?: number },
): boolean {
  if (q.type === "input" && text != null) {
    const stripped = text.trim();
    if (q.min_length && stripped && stripped.length < q.min_length) return false;
    if (q.max_words && stripped && stripped.split(/\s+/).filter(Boolean).length > q.max_words)
      return false;
  }
  if (q.type === "checkbox" && selectionCount != null) {
    if (q.max_selections && selectionCount > q.max_selections) return false;
    if (q.min_selections && selectionCount > 0 && selectionCount < q.min_selections) return false;
  }
  return true;
}

// True when this question would not block advancing: answered (and within
// constraints), or optional. Headings / page_breaks have no widget and never
// block. Mirrors SurveyDialog._question_satisfied.
function isQuestionSatisfied(q: SurveyQuestion, answer: Answer): boolean {
  if (!INPUT_TYPES.has(q.type)) return true;
  // A too-short / too-many answer blocks whether or not the question is required.
  if (q.type === "input") {
    const text = typeof answer === "string" ? answer : "";
    if (!answerMeetsConstraints(q, { text })) return false;
  } else if (q.type === "checkbox") {
    const count = Array.isArray(answer) ? answer.length : 0;
    if (!answerMeetsConstraints(q, { selectionCount: count })) return false;
  }
  // Optional questions (required === false) never block; only an unanswered
  // *required* question blocks.
  return isAnswered(q, answer) || q.required === false;
}

// True when every VISIBLE question on the page is satisfied (a hidden follow-up
// never gates, even if it is marked required).
function pageComplete(
  pageFlat: SurveyQuestion[],
  visible: Record<string, boolean>,
  answers: Record<string, Answer>,
): boolean {
  return pageFlat.every(
    (q) => !isVisible(visible, q.id) || isQuestionSatisfied(q, answers[q.id]),
  );
}

// ---------------------------------------------------------------------------
// Response strings (mirror SurveyDialog._submit_survey + str(response))
// ---------------------------------------------------------------------------

// Render a single string the way Python repr() does, so a checkbox list matches
// the desktop's str(list): single quotes by default, switching to double quotes
// when the label has an apostrophe but no double-quote; backslashes and the
// active quote are escaped.
function pyRepr(s: string): string {
  const useDouble = s.includes("'") && !s.includes('"');
  const quote = useDouble ? '"' : "'";
  let body = "";
  for (const ch of s) {
    if (ch === "\\") body += "\\\\";
    else if (ch === quote) body += "\\" + ch;
    else if (ch === "\n") body += "\\n";
    else if (ch === "\r") body += "\\r";
    else if (ch === "\t") body += "\\t";
    else body += ch;
  }
  return quote + body + quote;
}

// Match the desktop's stored string form so online + in-lab align. Checkbox is a
// Python-style list repr (the desktop logs str(list)): elements in DEFINITION
// order with Python repr quoting; others are plain.
export function toResponseString(q: SurveyQuestion, a: Answer): string {
  if (q.type === "checkbox" && Array.isArray(a)) {
    const opts = q.options ?? [];
    const inDef = opts.filter((o) => a.includes(o));
    const extras = a.filter((o) => !opts.includes(o));
    const ordered = [...inDef, ...extras];
    return "[" + ordered.map(pyRepr).join(", ") + "]";
  }
  if (typeof a === "number") return String(a);
  if (typeof a === "string") return a.trim();
  return "";
}

// The response string a submit records for one question, mirroring
// _submit_survey exactly: a hidden branch (visible false) records None, an
// unanswered VAS/likert/MC records None, an empty checkbox records "[]", and an
// empty input records "". str(None) is the literal "None".
function responseFor(q: SurveyQuestion, answer: Answer, visible: boolean): string {
  if (!visible) return "None";
  switch (q.type) {
    case "percentage_scale":
      return typeof answer === "number" ? String(answer) : "None";
    case "likert":
    case "multiple_choice":
      return typeof answer === "string" && answer.length > 0 ? answer : "None";
    case "checkbox":
      return toResponseString(q, Array.isArray(answer) ? answer : []);
    case "input":
      return typeof answer === "string" ? answer.trim() : "";
    default:
      return "";
  }
}

export type SurveyResponse = {
  question_id: string;
  response: string;
  // A smart question carries the check's run record (with answer_2 filled in);
  // it fills the sq_ columns server-side. Absent for every other type.
  smart?: Record<string, unknown> | undefined;
};

export function SurveyForm({
  questions,
  submitLabel = "OK",
  onSubmit,
  onSmartCheck,
}: {
  questions: SurveyQuestion[];
  submitLabel?: string;
  onSubmit: (responses: SurveyResponse[]) => void;
  // Bound to the session by the renderer; absent where there is no session
  // (then a smart question is a plain, unchecked text box). `provenance` is the
  // content-blind typing summary, sent only when provenance is enabled.
  onSmartCheck?: (
    questionId: string,
    answer: string,
    provenance?: Record<string, unknown>,
  ) => Promise<SmartCheckResult>;
}) {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [submitted, setSubmitted] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const setAnswer = (id: string, value: Answer) => setAnswers((m) => ({ ...m, [id]: value }));
  // Smart Question check state (Phase 5): the rich record per question, which
  // questions have been checked (don't re-check on a 2nd submit), the revealed
  // follow-up text per question, and whether a check is in flight.
  const [smartRecords, setSmartRecords] = useState<Record<string, Record<string, unknown>>>({});
  const [smartChecked, setSmartChecked] = useState<Set<string>>(new Set());
  const [followups, setFollowups] = useState<Record<string, string>>({});
  const [checking, setChecking] = useState(false);
  // Content-blind typing provenance — captured only when the participant was
  // shown the biometrics notice (provenance enabled). One recorder per smart
  // question, lazily created on first interaction.
  const provenanceOn = provenanceNoticeShown();
  const recorders = useRef<Record<string, ProvenanceRecorder>>({});
  const recorderFor = (qid: string): ProvenanceRecorder => {
    let r = recorders.current[qid];
    if (!r) {
      r = new ProvenanceRecorder();
      recorders.current[qid] = r;
    }
    return r;
  };
  // Capture handlers for a smart question's input, or undefined when off.
  const smartCaptureProps = (qid: string) => {
    if (!provenanceOn) return undefined;
    const rec = recorderFor(qid);
    return {
      onKeyDown: () => rec.recordKey(),
      onPaste: (e: ReactClipboardEvent<HTMLInputElement>) =>
        rec.recordPaste((e.clipboardData?.getData("text") ?? "").length),
      onFocus: () => rec.recordFocus(),
      onBlur: () => rec.recordBlur(),
    };
  };

  const pages = splitSurveyPages(questions);
  const pageFlats = pages.map(flattenQuestions);
  const visible = computeVisible(questions, answers);

  const totalPages = pages.length;
  const safeIndex = Math.min(pageIndex, totalPages - 1);
  const isLast = safeIndex === totalPages - 1;
  const multiPage = totalPages > 1;

  // A revealed follow-up inherits the smart question's required trait: once it is
  // shown, if the original question was required, the follow-up answer must be
  // filled before the page counts as complete (parity with the first answer). An
  // optional question's follow-up never gates.
  const smartFollowupsSatisfied = (pageFlat: SurveyQuestion[]): boolean =>
    pageFlat.every((q) => {
      if (q.type !== "smart_question") return true;
      if (!isVisible(visible, q.id)) return true;
      if (!followups[q.id]) return true; // no follow-up shown -> nothing to gate
      if (q.required === false) return true;
      return String(answers[`${q.id}__fu`] ?? "").trim() !== "";
    });

  const currentComplete =
    pageComplete(pageFlats[safeIndex], visible, answers) &&
    smartFollowupsSatisfied(pageFlats[safeIndex]);
  const allComplete = pageFlats.every(
    (pf) => pageComplete(pf, visible, answers) && smartFollowupsSatisfied(pf),
  );

  async function finish() {
    if (submitted || checking) return;
    if (!allComplete) return;

    // Run the Smart Question check(s) before finishing, when wired to a session.
    // Guarded: a survey with no smart questions (or no onSmartCheck) behaves
    // exactly as before - synchronous submit, no check.
    if (onSmartCheck) {
      const pending = pageFlats
        .flat()
        .filter(
          (q) =>
            q.type === "smart_question" &&
            isVisible(visible, q.id) &&
            !smartChecked.has(q.id) &&
            String(answers[q.id] ?? "").trim() !== "",
        );
      if (pending.length > 0) {
        setChecking(true);
        const nextChecked = new Set(smartChecked);
        const nextRecords = { ...smartRecords };
        const nextFollowups = { ...followups };
        let revealed = false;
        for (const q of pending) {
          try {
            const answerText = String(answers[q.id] ?? "");
            const prov = provenanceOn
              ? recorderFor(q.id).summarize(answerText)
              : undefined;
            const res = await onSmartCheck(q.id, answerText, prov);
            nextChecked.add(q.id);
            if (res.record) nextRecords[q.id] = res.record;
            if (res.followup_shown && res.followup_text) {
              nextFollowups[q.id] = res.followup_text;
              revealed = true;
            }
          } catch {
            // A failed check must never block the participant.
            nextChecked.add(q.id);
          }
        }
        setSmartChecked(nextChecked);
        setSmartRecords(nextRecords);
        setFollowups(nextFollowups);
        setChecking(false);
        if (revealed) return; // stay so the participant can answer the follow-up
      }
    }

    setSubmitted(true);
    const responses: SurveyResponse[] = [];
    for (const pageFlat of pageFlats) {
      for (const q of pageFlat) {
        if (!INPUT_TYPES.has(q.type)) continue;
        if (q.type === "smart_question") {
          const answer1 = String(answers[q.id] ?? "").trim();
          const answer2 = String(answers[`${q.id}__fu`] ?? "").trim();
          const rec = smartRecords[q.id]
            ? { ...smartRecords[q.id], answer_2: answer2 || null }
            : undefined;
          // The FINAL answer is recorded (follow-up answer if there was one).
          responses.push({
            question_id: q.id,
            response: answer2 || answer1,
            smart: rec,
          });
        } else {
          responses.push({
            question_id: q.id,
            response: responseFor(q, answers[q.id], isVisible(visible, q.id)),
          });
        }
      }
    }
    onSubmit(responses);
  }

  // One top-level question and its currently-visible follow-ups, recursively.
  const renderNode = (q: SurveyQuestion, nested: boolean): ReactNode => (
    <Fragment key={q.id}>
      <div className={"jol-survey-q" + (nested ? " jol-survey-q--nested" : "")}>
        {renderQuestion(
          q,
          answers[q.id],
          setAnswer,
          // Lock the original answer once its follow-up is shown: the check
          // already ran on it, so it must not change underneath the follow-up.
          q.type === "smart_question" && !!followups[q.id],
          q.type === "smart_question" ? smartCaptureProps(q.id) : undefined,
        )}
      </div>
      {q.type === "smart_question" && followups[q.id] ? (
        <div
          className="jol-survey-q"
          style={{
            border: "2px solid #8b5cf6",
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 8,
            background: "rgba(139,92,246,0.06)",
          }}
        >
          <div className="jol-survey-prompt">
            {followups[q.id]}
            {q.required !== false ? (
              <span className="jol-survey-required" aria-hidden="true">
                {" *"}
              </span>
            ) : null}
          </div>
          <input
            type="text"
            className="jol-survey-input"
            value={
              typeof answers[`${q.id}__fu`] === "string"
                ? (answers[`${q.id}__fu`] as string)
                : ""
            }
            onChange={(e) => setAnswer(`${q.id}__fu`, e.target.value)}
          />
        </div>
      ) : null}
      {(q.nested ?? []).map((child) =>
        isVisible(visible, child.id) ? renderNode(child, true) : null,
      )}
    </Fragment>
  );

  return (
    <div className="jol-survey-card">
      {checking ? (
        <div className="jol-survey-checking" role="status" aria-live="polite">
          <div className="jol-spinner" />
          <div className="jol-survey-checking-label">Checking your answer…</div>
        </div>
      ) : null}
      {multiPage ? (
        <div className="jol-survey-progress">
          <div className="jol-survey-progressbar">
            <div
              className="jol-survey-progressbar-fill"
              style={{ width: `${((safeIndex + 1) / totalPages) * 100}%` }}
            />
          </div>
          <div className="jol-survey-progresslabel">
            Page {safeIndex + 1} of {totalPages}
          </div>
        </div>
      ) : null}

      <div className="jol-survey-list">
        {pages[safeIndex].map((q) => renderNode(q, false))}
      </div>

      <div className={"jol-survey-footer" + (multiPage ? " jol-survey-footer--nav" : "")}>
        {multiPage ? (
          <button
            type="button"
            className="jol-survey-back"
            disabled={safeIndex === 0}
            onClick={() => setPageIndex(Math.max(0, safeIndex - 1))}
          >
            Back
          </button>
        ) : null}
        {multiPage && !isLast ? (
          <button
            type="button"
            className="jol-survey-submit"
            disabled={!currentComplete}
            onClick={() => setPageIndex(Math.min(totalPages - 1, safeIndex + 1))}
          >
            Next
          </button>
        ) : null}
        {isLast ? (
          <button
            type="button"
            className="jol-survey-submit"
            disabled={!allComplete || submitted || checking}
            onClick={() => {
              void finish();
            }}
          >
            {checking ? "Checking..." : submitted ? "Submitted" : submitLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// A question's prompt, with a red "*" appended when it is required (parity with
// the desktop _apply_question_label). Required is the default; only an explicit
// required === false is optional and shows no marker.
function questionPrompt(q: SurveyQuestion): ReactNode {
  const required = q.required !== false;
  return (
    <div className="jol-survey-prompt">
      {q.question ?? ""}
      {required ? (
        <span className="jol-survey-required" aria-hidden="true">
          {" *"}
        </span>
      ) : null}
    </div>
  );
}

type SmartInputProps = {
  onKeyDown?: () => void;
  onPaste?: (e: ReactClipboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

function renderQuestion(
  q: SurveyQuestion,
  answer: Answer,
  setAnswer: (id: string, value: Answer) => void,
  locked = false,
  smartInputProps?: SmartInputProps,
) {
  switch (q.type) {
    case "heading":
      return <div className="jol-survey-heading">{q.text ?? ""}</div>;

    case "percentage_scale": {
      const touched = typeof answer === "number";
      const value = touched ? (answer as number) : 0;
      const [left, right] = q.scale ?? ["", ""];
      const showBubble = q.show_value_bubble !== false;
      const showEndpoints = q.show_endpoints !== false;
      // Reveal + position the handle at the click point (matches the desktop
      // VASlider, whose handle is invisible until the participant clicks).
      const onDown = (e: ReactPointerEvent<HTMLInputElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = Math.max(
          0,
          Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)),
        );
        setAnswer(q.id, pct);
      };
      const trackBg = touched
        ? `linear-gradient(to right, #2f7bff 0%, #2f7bff ${value}%, #e6ecf6 ${value}%, #e6ecf6 100%)`
        : "#e6ecf6";
      return (
        <>
          {questionPrompt(q)}
          <div className="jol-vas">
            {touched && showBubble ? (
              <div className="jol-vas-bubble" style={{ left: `${value}%` }}>
                {value}%
              </div>
            ) : null}
            <input
              type="range"
              min={0}
              max={100}
              value={value}
              className={"jol-vas-slider" + (touched ? " jol-vas-slider--touched" : "")}
              style={{ background: trackBg }}
              onPointerDown={onDown}
              onChange={(e) => setAnswer(q.id, Number(e.target.value))}
            />
            <div className="jol-vas-scale">
              <span>
                {left} {showEndpoints ? <span className="jol-vas-pct">0%</span> : null}
              </span>
              <span>
                {showEndpoints ? <span className="jol-vas-pct">100%</span> : null} {right}
              </span>
            </div>
          </div>
        </>
      );
    }

    case "likert": {
      const points = q.points ?? 5;
      const anchors = q.anchors ?? {};
      const showNumbers = q.show_point_numbers !== false;
      const reverse = q.reverse_scale === true;
      // Points stay in value order (the recorded value is unchanged); only the
      // visual column order reverses.
      const order = Array.from({ length: points }, (_, i) => i + 1);
      if (reverse) order.reverse();
      return (
        <>
          {questionPrompt(q)}
          <div className="jol-likert">
            {order.map((num) => {
              const n = String(num);
              return (
                <label key={n} className="jol-likert-pt">
                  <span className="jol-likert-top">
                    <input
                      type="radio"
                      name={q.id}
                      checked={answer === n}
                      onChange={() => setAnswer(q.id, n)}
                    />
                    <span>{showNumbers ? n : ""}</span>
                  </span>
                  <span className="jol-likert-anchor">{anchors[n] ?? ""}</span>
                </label>
              );
            })}
          </div>
        </>
      );
    }

    case "multiple_choice":
      return (
        <>
          {questionPrompt(q)}
          {(q.options ?? []).map((opt, i) => (
            <label key={i} className="jol-survey-opt">
              <input
                type="radio"
                name={q.id}
                checked={answer === opt}
                onChange={() => setAnswer(q.id, opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </>
      );

    case "checkbox": {
      const selected = Array.isArray(answer) ? (answer as string[]) : [];
      return (
        <>
          {questionPrompt(q)}
          {(q.options ?? []).map((opt, i) => (
            <label key={i} className="jol-survey-opt">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={(e) =>
                  setAnswer(
                    q.id,
                    e.target.checked ? [...selected, opt] : selected.filter((o) => o !== opt),
                  )
                }
              />
              <span>{opt}</span>
            </label>
          ))}
        </>
      );
    }

    case "input":
      return (
        <>
          {questionPrompt(q)}
          <input
            type="text"
            className="jol-survey-input"
            placeholder={q.placeholder ?? ""}
            maxLength={q.max_length && q.max_length > 0 ? q.max_length : undefined}
            value={typeof answer === "string" ? answer : ""}
            onChange={(e) => setAnswer(q.id, filterByFlags(e.target.value, validationFlags(q)))}
          />
        </>
      );
    case "smart_question":
      // Renders as a plain open-text box; the AI check runs on submit (see
      // finish()), and a follow-up is revealed inline below it if it fires. Once
      // that happens the box is locked (readOnly) so the checked answer can't
      // change out from under its follow-up.
      return (
        <>
          {questionPrompt(q)}
          <input
            type="text"
            className="jol-survey-input"
            value={typeof answer === "string" ? answer : ""}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            readOnly={locked}
            aria-readonly={locked || undefined}
            style={
              locked
                ? { background: "var(--jol-bg-subtle)", cursor: "not-allowed" }
                : undefined
            }
            // Content-blind provenance capture (only present when provenance on).
            {...(locked ? {} : (smartInputProps ?? {}))}
          />
        </>
      );

    default:
      return null;
  }
}
